import { selectUiSettings } from '@App/app/auth/store/auth/selectors/auth.selectors';
import { TIMING } from '@App/app/configs/timing.config';
import { UiSettings } from '@App/app/entities/users/user';
import { getMemoryHeapUsage } from '@App/app/shared/utils/utils';
import { Store } from '@ngrx/store';
import { Observable, interval } from 'rxjs';
import { filter, first, map, startWith } from 'rxjs/operators';
import { GREY_COLOR, WHITE_COLOR } from '@App/app/configs/babylon.config';

export interface ViewerColors {
  background: string;
  viewfinder: string;
}

/** Emits the current preset's background/viewfinder colours once, falling back to defaults. */
export const buildUiSettingsColors$ = (store: Store): Observable<ViewerColors> =>
  store.select(selectUiSettings).pipe(
    first(),
    filter<UiSettings>(Boolean),
    map((uiSettings) => ({
      background: uiSettings.currentPreset?.backgroundColor || GREY_COLOR,
      viewfinder: uiSettings.currentPreset?.viewFinderColor || WHITE_COLOR,
    })),
  );

/** Polls heap usage on a fixed interval, reporting it as a percentage with two decimals. */
export const buildHeapSizeReport$ = (): Observable<number> =>
  interval(TIMING.HEAP_SIZE_POLL_INTERVAL_MS).pipe(
    startWith(0),
    map(() => +(getMemoryHeapUsage() * 100).toFixed(2)),
  );

/** Maps a raw cache-size stream to a `{ total, percentage }` view against the cache limit. */
export const buildTotalCacheSize$ = (
  source$: Observable<number>,
  maxCacheSize: number,
): Observable<{ total: number; percentage: number }> =>
  source$.pipe(
    map((total) => +total?.toFixed(2) ?? 0),
    map((total) => ({ total, percentage: +((total / maxCacheSize) * 100).toFixed(2) })),
  );
